import { MINUTES } from '@sequencehq/utils/dates'

import { stripSemanticTypeScope } from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import type { SimulationDependency } from '../../simulation.types'

/**
 * How long a cached answer counts as good enough. "Use the cached value" means a
 * few minutes, not forever, so if someone else edits a quote template we'll pick
 * that up on the next simulation rather than showing the old one all session.
 */
export const CACHED_DEPENDENCY_STALE_TIME = 5 * MINUTES

/**
 * How long we'll accept a cached answer for one task. Zero means go and ask.
 *
 * There's an 'exclusive' approach to this - useCacheValueFor - where we state
 * what we want to keep. Then we ignore the cache of everything else.
 * Secondly there's an 'inclusive' approach, where we instead state what we
 * want to refresh, and leave everything else be.
 */
export const staleTimeFor = ({
  dependency,
  useCacheValueFor,
  refresh
}: {
  dependency: SimulationDependency
  useCacheValueFor: string[]
  refresh: string[] | undefined
}): number => {
  if (refresh) {
    return refresh.includes(dependency.task.taskReferenceName)
      ? 0
      : CACHED_DEPENDENCY_STALE_TIME
  }

  const cacheable = useCacheValueFor.map(stripSemanticTypeScope)

  // A task we only fetched to feed another one publishes nothing of its own, and
  // an empty list would otherwise pass this check without being asked about at
  // all. Those follow whatever reads them, so we go and fetch.
  const isCacheable =
    dependency.semanticTypes.length > 0 &&
    dependency.semanticTypes.every(semanticType =>
      cacheable.includes(stripSemanticTypeScope(semanticType))
    )

  return isCacheable ? CACHED_DEPENDENCY_STALE_TIME : 0
}
